import React, { useRef } from 'react';
import { motion, useScroll, useTransform} from 'motion/react';
import { Link } from 'react-router-dom';
import { 
  Globe, 
  ArrowRight, 
  CheckCircle, 
  Smartphone, 
  TrendingUp, 
  Zap, 
  Users, 
  Clock,
  Shield,
  Eye,
  Play,
  Search,
  Code,
  Palette,
  Settings,
  CreditCard,
  Monitor,
  Calendar
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';
import { ProductTestimonials } from '../../shared/ProductTestimonials';
import { websitesTestimonials } from '../../shared/testimonialData';

const Websites: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  
 
  const features = [
    {
      icon: Smartphone,
      title: 'Responsive Design',
      description: 'Fully responsive websites that look stunning and function perfectly across all devices and screen sizes.'
    },
    {
      icon: Zap,
      title: 'Fast Loading',
      description: 'Optimized for speed with advanced caching, CDN integration, and lightweight code for superior performance.'
    },
    {
      icon: Search,
      title: 'SEO Optimized',
      description: 'Built-in SEO best practices including meta tags, structured data, and search engine optimization techniques.'
    },
    {
      icon: Shield,
      title: 'Security First',
      description: 'Enterprise-grade security with SSL certificates, secure hosting, and regular security updates and monitoring.'
    },
    {
      icon: Code,
      title: 'Latest Tech Stack',
      description: 'Built with modern technologies including React, Next.js, Node.js, and cloud-native architecture.'
    },
    {
      icon: Settings,
      title: 'CMS Integration',
      description: 'Easy content management with popular CMS platforms and custom admin panels for effortless updates.'
    }
  ];

  const websiteTypes = [
    {
      icon: Palette,
      title: 'Corporate Portfolios',
      description: 'Professional websites that showcase your company\'s expertise, services, and achievements.',
      features: ['Brand Showcase', 'Service Pages', 'Team Profiles', 'Contact Forms', 'Blog Integration']
    },
    {
      icon: CreditCard,
      title: 'E-commerce Stores',
      description: 'Feature-rich online stores with secure payment processing and inventory management.',
      features: ['Product Catalogs', 'Shopping Cart', 'Payment Gateway', 'Order Management', 'Customer Accounts']
    },
    {
      icon: Calendar,
      title: 'Booking Systems',
      description: 'Integrated booking and reservation systems for service-based businesses.',
      features: ['Online Booking', 'Calendar Integration', 'Payment Processing', 'Automated Reminders', 'Customer Management']
    },
    {
      icon: Users,
      title: 'Custom Portals',
      description: 'Tailored web applications and portals for specific business requirements.',
      features: ['User Authentication', 'Role-based Access', 'Custom Workflows', 'Data Management', 'API Integration']
    }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Drive Business Results',
      description: 'Websites designed to convert visitors into customers with strategic user experience and compelling design.',
      metric: '300% increase in conversions'
    },
    {
      icon: Users,
      title: 'Enhanced User Experience',
      description: 'Intuitive navigation and engaging interfaces that keep visitors engaged and coming back.',
      metric: '85% user satisfaction'
    },
    {
      icon: Search,
      title: 'Search Engine Visibility',
      description: 'Maximize your online presence with SEO-optimized websites that rank higher in search results.',
      metric: '250% more organic traffic'
    },
    {
      icon: Clock,
      title: 'Long-term Performance',
      description: 'Robust architecture and regular maintenance ensure your website performs optimally for years.',
      metric: '99.9% uptime guarantee'
    }
  ];

  const technologies = [
    {
      category: 'Frontend',
      tools: ['React', 'Next.js', 'Vue.js', 'Angular', 'TypeScript', 'Tailwind CSS']
    },
    {
      category: 'Backend',
      tools: ['Node.js', 'Python', 'PHP', 'Laravel', 'Express.js', 'API Development']
    },
    {
      category: 'CMS & E-commerce',
      tools: ['WordPress', 'Shopify', 'WooCommerce', 'Strapi', 'Contentful', 'Sanity']
    },
    {
      category: 'Cloud & Hosting',
      tools: ['AWS', 'Vercel', 'Netlify', 'Google Cloud', 'Azure', 'CDN Integration']
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Discovery & Planning',
      description: 'Understanding your business goals, target audience, and technical requirements to create a comprehensive project roadmap.'
    },
    {
      step: '02',
      title: 'Design & Prototyping',
      description: 'Creating wireframes, mockups, and interactive prototypes that align with your brand and user experience goals.'
    },
    {
      step: '03',
      title: 'Development & Testing',
      description: 'Building your website with clean, scalable code and rigorous testing across all devices and browsers.'
    },
    {
      step: '04',
      title: 'Launch & Optimization',
      description: 'Deploying your website with performance monitoring, analytics setup, and ongoing optimization for best results.'
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 10 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-4 h-4 bg-purple-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 6,
                delay: i * 0.5,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 neon-glow">
                  <Globe className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-purple-400 uppercase tracking-wide">Digital Excellence Delivered</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    Professional <span className="bg-gradient-to-r from-purple-500 to-violet-600 bg-clip-text text-transparent">Websites</span>
                  </h1>
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed"
              >
                We create visually stunning, fast-loading, and secure websites that drive results. Whether it's a corporate portfolio, 
                e-commerce store, booking system, or custom portal, our solutions are fully responsive, SEO-optimized, and built with the latest technologies.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-purple-500 to-violet-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-purple-500/25 transition-all duration-300"
                >
                  Start Your Project
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  View Portfolio
                </button>
              </motion.div>

              {/* Key Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="grid grid-cols-3 gap-8 hidden"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-500">500+</div>
                  <div className="text-sm text-muted-foreground">Websites Built</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-violet-400">99.9%</div>
                  <div className="text-sm text-muted-foreground">Uptime</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">2sec</div>
                  <div className="text-sm text-muted-foreground">Load Time</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative perspective-1000"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
                  className="absolute -inset-10 bg-gradient-to-r from-purple-500 to-violet-600 opacity-20 rounded-full blur-3xl"
                />
                
                <motion.div
                  whileHover={{ scale: 1.05, rotateY: 5, rotateX: 5 }}
                  transition={{ duration: 0.4 }}
                  className="relative z-10 glass-strong rounded-2xl overflow-hidden transform-gpu"
                >
                  <ImageWithFallback
                    src='/assets/Products/it-Products/websites.png'
                    alt="Professional Website Design"
                    className="w-full h-96 object-cover"
                  />
                  
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent">
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="flex items-center gap-3 text-white">
                        <div className="p-2 bg-purple-500/20 rounded-lg backdrop-blur-sm">
                          <Globe className="w-5 h-5 text-purple-500" />
                        </div>
                        <div>
                          <p className="font-semibold">Web-Powered</p>
                          <p className="text-sm text-white/80">Digital Solutions</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1 }}
                  className="absolute -top-4 -right-4 glass p-3 rounded-xl"
                >
                  <div className="flex items-center gap-2 text-purple-500">
                    <Monitor className="w-4 h-4 animate-pulse" />
                    <span className="text-xs font-mono">LIVE</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Website Types Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Comprehensive Website Solutions
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              From corporate portfolios to complex e-commerce platforms, we deliver tailored web solutions that meet your specific business needs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {websiteTypes.map((type, index) => {
              const Icon = type.icon;
              return (
                <motion.div
                  key={type.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="p-3 rounded-lg bg-purple-500/20">
                      <Icon className="w-8 h-8 text-purple-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{type.title}</h3>
                      <p className="text-muted-foreground">{type.description}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {type.features.map((feature) => (
                      <div key={feature} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-violet-400" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="relative py-15 hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Our Development Process
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              A proven methodology that ensures your website is delivered on time, within budget, and exceeds your expectations.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300">
                  <div className="text-3xl font-bold text-purple-500 mb-4">{step.step}</div>
                  <h3 className="text-lg font-semibold mb-3">{step.title}</h3>
                  <p className="text-muted-foreground text-sm">{step.description}</p>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-purple-500 to-violet-600"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Built for Performance & Growth
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              Every website we create is optimized for speed, security, and search engines to ensure long-term success and growth.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="mb-4">
                    <Icon className="w-8 h-8 text-purple-500" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Measurable Business Impact
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              Our websites are designed to drive real business results with improved conversions, user engagement, and search visibility.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-purple-500/20">
                      <Icon className="w-6 h-6 text-purple-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                      <p className="text-muted-foreground mb-3">{benefit.description}</p>
                      <div className="text-violet-400 font-semibold">{benefit.metric}</div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Modern Technology Stack
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              We use the latest technologies and frameworks to ensure your website is fast, secure, and future-proof.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {technologies.map((tech, index) => (
              <motion.div
                key={tech.category}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300"
              >
                <h3 className="text-lg font-semibold mb-4 text-purple-500">{tech.category}</h3>
                <div className="space-y-2">
                  {tech.tools.map((tool) => (
                    <div key={tool} className="text-sm text-muted-foreground">{tool}</div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-24 hidden">
        <ProductTestimonials 
          testimonials={websitesTestimonials}
          title="Client Success Stories"
          subtitle="See how businesses transformed their digital presence with our websites"
          gradientFrom="#8A3FFC"
          gradientVia="#A855F7"
          gradientTo="#8A3FFC"
          iconColor="#8A3FFC"
          dotColor="bg-purple-500"
          speed={65}
          direction="left"
        />
      </section>

      {/* CTA Section */}
      <section className="relative py-15">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready to Launch Your Website?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Transform your digital presence with a professional website that drives results. Let's bring your vision to life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-purple-500 to-violet-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-purple-500/25 transition-all duration-300"
              >
                Start Your Project
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300"
              >
                View Portfolio
                <Eye className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export { Websites };